import React, { useEffect, useState, useCallback } from 'react';
import {
  AreaChart, Area, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import api from '../../utils/api';

/* ─── Constantes de color (no CSS vars, Recharts necesita valores reales) ─── */
const C = {
  gold:    '#C9A84C',
  green:   '#4CAF50',
  red:     '#E53935',
  blue:    '#42A5F5',
  purple:  '#AB47BC',
  muted:   '#888888',
  surface: 'rgba(255,255,255,0.04)',
  border:  'rgba(255,255,255,0.08)',
  text:    '#E8E0D0',
};

const PIE_COLORS = [C.green, C.red, C.muted, C.blue, C.purple, C.gold];

/* ─── Helpers ─── */
const fmtCurrency = n => new Intl.NumberFormat('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 }).format(n || 0);
const toISO = d => d.toISOString().split('T')[0];
const getThisMonth = () => {
  const now = new Date();
  return {
    from: toISO(new Date(now.getFullYear(), now.getMonth(), 1)),
    to:   toISO(new Date(now.getFullYear(), now.getMonth() + 1, 0)),
  };
};
const getToday = () => { const d = toISO(new Date()); return { from: d, to: d }; };
const getThisWeek = () => {
  const now = new Date();
  const diff = (now.getDay() === 0 ? -6 : 1 - now.getDay());
  const mon = new Date(now); mon.setDate(now.getDate() + diff);
  const sun = new Date(mon); sun.setDate(mon.getDate() + 6);
  return { from: toISO(mon), to: toISO(sun) };
};
const getLast3 = () => {
  const to   = new Date();
  const from = new Date(); from.setMonth(from.getMonth() - 2); from.setDate(1);
  return { from: toISO(from), to: toISO(to) };
};

/* ─── Tooltip personalizado ─── */
const CustomTooltip = ({ active, payload, label, currency }) => {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: '#1e1e1e', border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', fontSize: '0.8rem' }}>
      <p style={{ color: C.text, fontWeight: 600, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => (
        <div key={i} style={{ color: p.color, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span>{p.name}</span>
          <strong>{currency ? fmtCurrency(p.value) : p.value}</strong>
        </div>
      ))}
    </div>
  );
};

/* ─── KPI Card ─── */
const KpiCard = ({ label, value, color, sub, icon }) => (
  <div className="stat-card" style={{ flex: '1 1 160px' }}>
    <div className="stat-icon" style={{ color }}>{icon}</div>
    <div>
      <div className="stat-value" style={{ color, fontSize: '1.35rem' }}>{value}</div>
      <div className="stat-label">{label}</div>
      {sub && <div style={{ fontSize: '0.73rem', color: C.muted, marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

/* ─── Gráfico vacío ─── */
const EmptyChart = () => (
  <div style={{ height: 220, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, fontSize: '0.85rem' }}>
    Sin datos para este período
  </div>
);

/* ─── Barra de progreso horizontal ─── */
const ProgressBar = ({ value, max, color = C.gold }) => (
  <div style={{ height: 6, background: C.border, borderRadius: 4, overflow: 'hidden', flex: 1 }}>
    <div style={{ width: `${max > 0 ? Math.round((value / max) * 100) : 0}%`, height: '100%', background: color, borderRadius: 4, transition: 'width 0.5s' }} />
  </div>
);

/* ─── Exportar Excel ─── */
const exportExcel = async (tab, data, from, to) => {
  const XLSX = await import('xlsx');
  let rows = [];
  if (tab === 'appointments') rows = data.map(r => ({ Fecha: r.fecha_cita || r.appointment_date, Total: r.total, Atendidas: r.attended, 'No asistió': r.not_attended, Canceladas: r.cancelled, Reagendadas: r.rescheduled, Ingresos: r.revenue || 0 }));
  else if (tab === 'services')     rows = data.map(r => ({ Servicio: r.service_name, Categoria: r.category, Vendidas: r.total_sold, Ejecutadas: r.executed, Ingresos: r.revenue }));
  else if (tab === 'workers')      rows = data.map(r => ({ Barbero: r.worker_name, 'Total citas': r.total_appointments, Atendidas: r.attended, Ingresos: r.revenue }));
  const ws = XLSX.utils.json_to_sheet(rows);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
  XLSX.writeFile(wb, `reporte-${tab}-${from}-${to}.xlsx`);
};

/* ═══════════════════════════════════════════════════════════════════════════ */
export default function ReportsPage() {
  const defaultDates = getThisMonth();
  const [tab, setTab]               = useState('appointments');
  const [from, setFrom]             = useState(defaultDates.from);
  const [to, setTo]                 = useState(defaultDates.to);
  const [activePeriod, setActivePeriod] = useState('month');
  const [data, setData]             = useState([]);
  const [loading, setLoading]       = useState(false);
  const [exporting, setExporting]   = useState('');
  const [chartType, setChartType]   = useState('area'); // area | bar

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/reports/${tab}`, { params: { from, to } })
      .then(r => setData(r.data))
      .catch(() => setData([]))
      .finally(() => setLoading(false));
  }, [tab, from, to]);

  useEffect(() => { load(); }, [load]);

  const applyPeriod = (period) => {
    setActivePeriod(period);
    const map = { today: getToday(), week: getThisWeek(), month: getThisMonth(), last3: getLast3() };
    const d = map[period];
    if (d) { setFrom(d.from); setTo(d.to); }
  };

  const handleExport = async (type) => {
    if (!data.length) return;
    setExporting(type);
    try {
      if (type === 'excel') await exportExcel(tab, data, from, to);
      else {
        const { default: jsPDF } = await import('jspdf');
        const { default: autoTable } = await import('jspdf-autotable');
        const doc = new jsPDF();
        const titles = { appointments: 'Reporte de Citas', services: 'Reporte de Servicios', workers: 'Productividad Barberos' };
        doc.setFontSize(16); doc.text(titles[tab], 14, 18);
        doc.setFontSize(10); doc.text(`Periodo: ${from} al ${to}`, 14, 26);
        let head = [], body = [];
        if (tab === 'appointments') { head = [['Fecha','Total','Atendidas','No asistió','Canceladas','Reagendadas','Ingresos']]; body = data.map(r => [r.fecha_cita||r.appointment_date,r.total,r.attended,r.not_attended,r.cancelled,r.rescheduled,fmtCurrency(r.revenue||0)]); }
        else if (tab === 'services') { head = [['Servicio','Categoria','Vendidas','Ejecutadas','Ingresos']]; body = data.map(r => [r.service_name,r.category,r.total_sold,r.executed,fmtCurrency(r.revenue)]); }
        else if (tab === 'workers')  { head = [['Barbero','Total citas','Atendidas','Ingresos']]; body = data.map(r => [r.worker_name,r.total_appointments,r.attended,fmtCurrency(r.revenue)]); }
        autoTable(doc, { head, body, startY: 32, styles: { fontSize: 9 }, headStyles: { fillColor: [79,70,229] } });
        doc.save(`reporte-${tab}-${from}-${to}.pdf`);
      }
    } finally { setExporting(''); }
  };

  /* ─── Derivados por tab ─── */
  const apptTotal     = data.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const apptAttended  = data.reduce((s, r) => s + (Number(r.attended) || 0), 0);
  const apptCancelled = data.reduce((s, r) => s + (Number(r.cancelled) || 0), 0);
  const apptRevenue   = data.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
  const apptPct       = apptTotal > 0 ? Math.round((apptAttended / apptTotal) * 100) : 0;

  const apptPieData = [
    { name: 'Atendidas',   value: apptAttended },
    { name: 'Canceladas',  value: apptCancelled },
    { name: 'No asistió',  value: data.reduce((s, r) => s + (Number(r.not_attended) || 0), 0) },
    { name: 'Reagendadas', value: data.reduce((s, r) => s + (Number(r.rescheduled) || 0), 0) },
  ].filter(d => d.value > 0);

  const svcRevTotal  = data.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
  const svcTopBar    = [...data].sort((a, b) => (b.executed || 0) - (a.executed || 0)).slice(0, 10).map(r => ({ name: r.service_name, Ejecutadas: r.executed, Ingresos: Number(r.revenue) || 0 }));
  const svcPieData   = data.map((r, i) => ({ name: r.service_name, value: Number(r.revenue) || 0, color: PIE_COLORS[i % PIE_COLORS.length] })).filter(d => d.value > 0).slice(0, 6);

  const wrkMaxAppt   = Math.max(...data.map(r => Number(r.total_appointments) || 0), 1);
  const wrkTotalRev  = data.reduce((s, r) => s + (Number(r.revenue) || 0), 0);
  const wrkBarData   = data.map(r => ({ name: r.worker_name, Citas: Number(r.total_appointments) || 0, Ingresos: Number(r.revenue) || 0 }));

  const periodBtns = [
    { key: 'today', label: 'Hoy' },
    { key: 'week',  label: 'Semana' },
    { key: 'month', label: 'Mes' },
    { key: 'last3', label: 'Últimos 3 meses' },
  ];

  const pillStyle = (active) => ({
    padding: '5px 13px', borderRadius: 20, border: 'none', cursor: 'pointer',
    fontSize: '0.78rem', fontFamily: 'var(--font-body)',
    background: active ? C.gold : 'var(--color-surface)',
    color: active ? '#000' : 'var(--color-text)',
    fontWeight: active ? 700 : 400, transition: 'all 0.15s',
  });

  const tabStyle = (key) => ({
    padding: '10px 18px', background: 'none', border: 'none', cursor: 'pointer',
    color: tab === key ? C.gold : C.muted,
    borderBottom: tab === key ? `2px solid ${C.gold}` : '2px solid transparent',
    fontFamily: 'var(--font-body)', fontSize: '0.875rem', fontWeight: tab === key ? 600 : 400,
    marginBottom: -1, transition: 'color 0.15s',
  });

  const sectionTitle = (title, subtitle) => (
    <div style={{ marginBottom: 14 }}>
      <div style={{ fontWeight: 700, fontSize: '0.9rem', color: C.text }}>{title}</div>
      {subtitle && <div style={{ fontSize: '0.75rem', color: C.muted, marginTop: 2 }}>{subtitle}</div>}
    </div>
  );

  return (
    <>
      {/* Header */}
      <div className="page-header">
        <div className="page-header-text">
          <h2>Reportes</h2>
          <p>Análisis operativo y de desempeño</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" disabled={!data.length || exporting === 'excel'} onClick={() => handleExport('excel')}>
            {exporting === 'excel' ? 'Exportando...' : '📥 Excel'}
          </button>
          <button className="btn btn-secondary" disabled={!data.length || exporting === 'pdf'} onClick={() => handleExport('pdf')}>
            {exporting === 'pdf' ? 'Exportando...' : '📄 PDF'}
          </button>
        </div>
      </div>

      {/* Filtros */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div>
            <div style={{ fontSize: '0.72rem', color: C.muted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Período rápido</div>
            <div style={{ display: 'flex', gap: 6 }}>
              {periodBtns.map(b => (
                <button key={b.key} style={pillStyle(activePeriod === b.key)} onClick={() => applyPeriod(b.key)}>{b.label}</button>
              ))}
            </div>
          </div>
          <div style={{ width: 1, background: 'var(--color-border)', alignSelf: 'stretch', margin: '0 4px' }} />
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Desde</label>
            <input type="date" className="form-input" value={from} onChange={e => { setFrom(e.target.value); setActivePeriod(''); }} />
          </div>
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="form-label">Hasta</label>
            <input type="date" className="form-input" value={to} onChange={e => { setTo(e.target.value); setActivePeriod(''); }} />
          </div>
          <button className="btn btn-primary" onClick={load}>Aplicar</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--color-border)' }}>
        {[{ key: 'appointments', label: '📅 Citas' }, { key: 'services', label: '💈 Servicios' }, { key: 'workers', label: '👤 Productividad' }].map(t => (
          <button key={t.key} style={tabStyle(t.key)} onClick={() => setTab(t.key)}>{t.label}</button>
        ))}
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 64 }}><div className="spinner" style={{ margin: '0 auto' }} /></div>
      ) : data.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📊</div>
          <h3>Sin datos para el período</h3>
          <p>Ajusta el rango de fechas para ver información.</p>
        </div>
      ) : (

        /* ═══════════════ CITAS ═══════════════ */
        tab === 'appointments' ? (
          <>
            {/* KPIs */}
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
              <KpiCard icon="📅" label="Total citas"   value={apptTotal}              color={C.text} />
              <KpiCard icon="✅" label="Atendidas"     value={apptAttended}            color={C.green} sub={`${apptPct}% tasa de asistencia`} />
              <KpiCard icon="❌" label="Canceladas"    value={apptCancelled}           color={C.red} />
              <KpiCard icon="💰" label="Ingresos"      value={fmtCurrency(apptRevenue)} color={C.gold} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 16 }}>
              {/* Evolución de citas */}
              <div className="card">
                {sectionTitle('Evolución de citas por día')}
                <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
                  <button style={pillStyle(chartType === 'area')} onClick={() => setChartType('area')}>Área</button>
                  <button style={pillStyle(chartType === 'bar')}  onClick={() => setChartType('bar')}>Barras</button>
                </div>
                {data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    {chartType === 'area' ? (
                      <AreaChart data={data.map(r => ({ fecha: r.fecha_cita || r.appointment_date, Atendidas: Number(r.attended)||0, Canceladas: Number(r.cancelled)||0, 'No asistió': Number(r.not_attended)||0 }))}>
                        <defs>
                          <linearGradient id="gradGreen" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.green} stopOpacity={0.35}/><stop offset="95%" stopColor={C.green} stopOpacity={0}/></linearGradient>
                          <linearGradient id="gradRed"   x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor={C.red}   stopOpacity={0.25}/><stop offset="95%" stopColor={C.red}   stopOpacity={0}/></linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis dataKey="fecha" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} />
                        <YAxis tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Area type="monotone" dataKey="Atendidas"   stroke={C.green} fill="url(#gradGreen)" strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="Canceladas"  stroke={C.red}   fill="url(#gradRed)"   strokeWidth={2} dot={false} />
                        <Area type="monotone" dataKey="No asistió"  stroke={C.muted} fill="transparent"     strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
                      </AreaChart>
                    ) : (
                      <BarChart data={data.map(r => ({ fecha: r.fecha_cita || r.appointment_date, Atendidas: Number(r.attended)||0, Canceladas: Number(r.cancelled)||0 }))}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis dataKey="fecha" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} />
                        <YAxis tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="Atendidas"  fill={C.green} radius={[3,3,0,0]} />
                        <Bar dataKey="Canceladas" fill={C.red}   radius={[3,3,0,0]} />
                      </BarChart>
                    )}
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>

              {/* Distribución por estado */}
              <div className="card">
                {sectionTitle('Distribución por estado', 'Proporción de resultados del período')}
                {apptPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <PieChart>
                      <Pie data={apptPieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                        {apptPieData.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => [v, 'Citas']} contentStyle={{ background: '#1e1e1e', border: `1px solid ${C.border}`, borderRadius: 8 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>

              {/* Ingresos por día */}
              <div className="card" style={{ gridColumn: '1 / -1' }}>
                {sectionTitle('Ingresos por día')}
                {data.some(r => (r.revenue || 0) > 0) ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={data.map(r => ({ fecha: r.fecha_cita || r.appointment_date, Ingresos: Number(r.revenue)||0 }))}>
                      <defs>
                        <linearGradient id="gradGold" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={C.gold} stopOpacity={1} />
                          <stop offset="100%" stopColor={C.gold} stopOpacity={0.4} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="fecha" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} />
                      <YAxis tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => fmtCurrency(v).replace('COP','').trim()} />
                      <Tooltip content={<CustomTooltip currency />} />
                      <Bar dataKey="Ingresos" fill="url(#gradGold)" radius={[4,4,0,0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>
            </div>

            {/* Tabla detalle */}
            <div className="card">
              {sectionTitle('Detalle por fecha')}
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>Fecha</th><th>Total</th><th>Atendidas</th><th>No asistió</th><th>Canceladas</th><th>Reagendadas</th><th>Ingresos</th></tr></thead>
                  <tbody>
                    {data.map((r, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 500 }}>{r.fecha_cita || r.appointment_date}</td>
                        <td style={{ fontWeight: 600 }}>{r.total}</td>
                        <td style={{ color: C.green }}>{r.attended}</td>
                        <td style={{ color: C.muted }}>{r.not_attended}</td>
                        <td style={{ color: C.red }}>{r.cancelled}</td>
                        <td style={{ color: C.blue }}>{r.rescheduled}</td>
                        <td style={{ color: C.gold, fontWeight: 600 }}>{fmtCurrency(r.revenue || 0)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>

        /* ═══════════════ SERVICIOS ═══════════════ */
        ) : tab === 'services' ? (
          <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
              <KpiCard icon="💈" label="Servicios activos" value={data.length}                color={C.text} />
              <KpiCard icon="✅" label="Total ejecutados"  value={data.reduce((s,r) => s+(Number(r.executed)||0),0)} color={C.green} />
              <KpiCard icon="💰" label="Ingresos totales"  value={fmtCurrency(svcRevTotal)}   color={C.gold} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 16 }}>
              {/* Top servicios horizontal */}
              <div className="card" style={{ gridColumn: '1 / -1' }}>
                {sectionTitle('Top servicios por ejecución', 'Ordenados por número de servicios ejecutados')}
                {svcTopBar.length > 0 ? (
                  <ResponsiveContainer width="100%" height={Math.max(200, svcTopBar.length * 40)}>
                    <BarChart data={svcTopBar} layout="vertical" margin={{ left: 16 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} horizontal={false} />
                      <XAxis type="number" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
                      <YAxis type="category" dataKey="name" tick={{ fill: C.text, fontSize: 11 }} tickLine={false} width={130} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="Ejecutadas" fill={C.green}  radius={[0,4,4,0]} barSize={14} />
                      <Bar dataKey="Ingresos"   fill={C.gold}   radius={[0,4,4,0]} barSize={14} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>

              {/* Distribución de ingresos */}
              <div className="card">
                {sectionTitle('Distribución de ingresos por servicio')}
                {svcPieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={svcPieData} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => percent > 0.05 ? `${(percent*100).toFixed(0)}%` : ''}>
                        {svcPieData.map((d, i) => <Cell key={i} fill={d.color} />)}
                      </Pie>
                      <Tooltip formatter={v => [fmtCurrency(v), 'Ingresos']} contentStyle={{ background: '#1e1e1e', border: `1px solid ${C.border}`, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>

              {/* Ranking */}
              <div className="card">
                {sectionTitle('Ranking por ingresos')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  {[...data].sort((a,b) => (b.revenue||0)-(a.revenue||0)).slice(0,7).map((r, i) => (
                    <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <span style={{ minWidth: 20, fontWeight: 700, color: i < 3 ? C.gold : C.muted, fontSize: '0.8rem' }}>#{i+1}</span>
                      <span style={{ flex: 1, fontSize: '0.83rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.service_name}</span>
                      <ProgressBar value={r.revenue||0} max={data[0]?.revenue||1} />
                      <span style={{ minWidth: 70, textAlign: 'right', fontSize: '0.8rem', color: C.gold, fontWeight: 600 }}>{fmtCurrency(r.revenue||0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Tabla */}
            <div className="card">
              {sectionTitle('Detalle de servicios')}
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>#</th><th>Servicio</th><th>Categoría</th><th>Vendidas</th><th>Ejecutadas</th><th>Ingresos</th></tr></thead>
                  <tbody>
                    {[...data].sort((a,b)=>(b.revenue||0)-(a.revenue||0)).map((r, i) => (
                      <tr key={i}>
                        <td><span className="badge badge-gold">{i+1}</span></td>
                        <td style={{ fontWeight: 500 }}>{r.service_name}</td>
                        <td style={{ color: C.muted }}>{r.category}</td>
                        <td>{r.total_sold}</td>
                        <td style={{ color: C.green }}>{r.executed}</td>
                        <td style={{ color: C.gold, fontWeight: 600 }}>{fmtCurrency(r.revenue)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>

        /* ═══════════════ TRABAJADORES ═══════════════ */
        ) : (
          <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginBottom: 20 }}>
              <KpiCard icon="👤" label="Barberos"        value={data.length}                  color={C.text} />
              <KpiCard icon="📅" label="Total citas"     value={data.reduce((s,r)=>s+(Number(r.total_appointments)||0),0)} color={C.blue} />
              <KpiCard icon="💰" label="Ingresos totales" value={fmtCurrency(wrkTotalRev)}    color={C.gold} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 16, marginBottom: 16 }}>
              {/* Citas e ingresos por barbero */}
              <div className="card" style={{ gridColumn: '1 / -1' }}>
                {sectionTitle('Citas e ingresos por barbero')}
                {wrkBarData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={wrkBarData} margin={{ left: 8 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                      <XAxis dataKey="name" tick={{ fill: C.text, fontSize: 11 }} tickLine={false} />
                      <YAxis yAxisId="left"  tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: C.muted, fontSize: 10 }} tickLine={false} axisLine={false} tickFormatter={v => `$${(v/1000).toFixed(0)}k`} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar yAxisId="left"  dataKey="Citas"    fill={C.blue} radius={[4,4,0,0]} barSize={20} />
                      <Bar yAxisId="right" dataKey="Ingresos" fill={C.gold} radius={[4,4,0,0]} barSize={20} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>

              {/* Ranking barberos */}
              <div className="card">
                {sectionTitle('Ranking por ingresos')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  {[...data].sort((a,b)=>(b.revenue||0)-(a.revenue||0)).map((r, i) => (
                    <div key={i}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          {i < 3 ? ['🥇','🥈','🥉'][i] : `#${i+1}`} {r.worker_name}
                        </span>
                        <span style={{ fontSize: '0.82rem', color: C.gold, fontWeight: 600 }}>{fmtCurrency(r.revenue||0)}</span>
                      </div>
                      <ProgressBar value={r.revenue||0} max={data[0]?.revenue||1} />
                      <div style={{ display: 'flex', gap: 16, marginTop: 4, fontSize: '0.72rem', color: C.muted }}>
                        <span>{r.total_appointments} citas</span>
                        <span style={{ color: C.green }}>{r.attended} atendidas</span>
                        <span>{r.total_appointments > 0 ? Math.round((r.attended/r.total_appointments)*100) : 0}% eficiencia</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Eficiencia (pie) */}
              <div className="card">
                {sectionTitle('Eficiencia por barbero', 'Proporción de citas atendidas')}
                {data.length > 0 ? (
                  <ResponsiveContainer width="100%" height={240}>
                    <PieChart>
                      <Pie data={data.map((r,i) => ({ name: r.worker_name, value: Number(r.attended)||0, color: PIE_COLORS[i%PIE_COLORS.length] }))} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, percent }) => percent > 0.08 ? `${(percent*100).toFixed(0)}%` : ''}>
                        {data.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={v => [v, 'Atendidas']} contentStyle={{ background: '#1e1e1e', border: `1px solid ${C.border}`, borderRadius: 8 }} />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <EmptyChart />}
              </div>
            </div>

            {/* Tabla */}
            <div className="card">
              {sectionTitle('Detalle de productividad')}
              <div className="table-wrapper">
                <table>
                  <thead><tr><th>#</th><th>Barbero</th><th>Total citas</th><th>Atendidas</th><th>Eficiencia</th><th>Ingresos</th></tr></thead>
                  <tbody>
                    {[...data].sort((a,b)=>(b.revenue||0)-(a.revenue||0)).map((r, i) => {
                      const pct = r.total_appointments > 0 ? Math.round((r.attended/r.total_appointments)*100) : 0;
                      return (
                        <tr key={i}>
                          <td><span className="badge badge-gold">{i+1}</span></td>
                          <td style={{ fontWeight: 600 }}>{r.worker_name}</td>
                          <td>{r.total_appointments}</td>
                          <td style={{ color: C.green }}>{r.attended}</td>
                          <td>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <ProgressBar value={pct} max={100} color={pct >= 80 ? C.green : pct >= 50 ? C.gold : C.red} />
                              <span style={{ fontSize: '0.78rem', minWidth: 34, color: pct >= 80 ? C.green : pct >= 50 ? C.gold : C.red, fontWeight: 600 }}>{pct}%</span>
                            </div>
                          </td>
                          <td style={{ color: C.gold, fontWeight: 600 }}>{fmtCurrency(r.revenue||0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )
      )}
    </>
  );
}
